'use client';

import { useEffect, useRef } from 'react';

export const useVisibilityInterval = (
    callback: () => void,
    interval: number,
) => {
    const intervalIdRef = useRef<number | undefined>(undefined);
    const elementRef = useRef<HTMLDivElement | null>(null);

    const callbackRef = useRef(callback);

    useEffect(() => {
        if (callbackRef.current !== callback) {
            callbackRef.current = callback;
            clearInterval(intervalIdRef.current);
            intervalIdRef.current = window.setInterval(() => {
                callbackRef.current();
            }, interval);
        }
    }, [callback, interval]);

    useEffect(() => {
        const observerCallback: IntersectionObserverCallback = entries => {
            entries.forEach(entry => {
                if (entry.isIntersecting && entry.intersectionRatio === 1) {
                    if (!intervalIdRef.current) {
                        intervalIdRef.current = window.setInterval(() => {
                            callbackRef.current();
                        }, interval);
                    }
                } else {
                    if (intervalIdRef.current) {
                        clearInterval(intervalIdRef.current);
                        intervalIdRef.current = undefined;
                    }
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, {
            threshold: 1.0,
        });

        if (elementRef.current) {
            observer.observe(elementRef.current);
        }

        return () => {
            if (intervalIdRef.current) {
                clearInterval(intervalIdRef.current);
            }
            if (elementRef.current) {
                observer.disconnect();
            }
        };
    }, [interval]);

    return elementRef;
};
